import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, FormHelperText, Typography } from '@mui/material';

const RichTextEditor = ({ 
  value, 
  onChange, 
  label, 
  error, 
  helperText, 
  required,
  placeholder = 'Enter text...',
  height = '150px'
}) => {
  // Function to convert HTML text content to uppercase while preserving tags
  const convertToUpperCase = (html) => {
    if (!html) return html;
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Function to recursively uppercase text nodes
    const uppercaseTextNodes = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = node.textContent.toUpperCase();
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        Array.from(node.childNodes).forEach(uppercaseTextNodes);
      }
    };
    
    uppercaseTextNodes(tempDiv);
    return tempDiv.innerHTML;
  };

  // Handle onChange to convert content to uppercase
  const handleChange = (content) => {
    const uppercasedContent = convertToUpperCase(content);
    onChange(uppercasedContent);
  };

  // Configure toolbar with simple formatting options only (no file upload)
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['clean']
    ],
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'indent',
    'align'
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography 
          variant="body2"
          sx={{ 
            mb: 1, 
            display: 'block',
            color: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          {label} {required && <span style={{ color: '#d32f2f' }}>*</span>}
        </Typography>
      )}
      <Box
        sx={{
          width: '100%',
          '& .quill': {
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: error ? '2px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
            overflow: 'hidden',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            },
            '&:focus-within': {
              borderColor: error ? '#d32f2f' : '#00798E',
              borderWidth: '2px',
              boxShadow: error 
                ? '0 0 0 3px rgba(211, 47, 47, 0.1)' 
                : '0 0 0 3px rgba(0, 121, 142, 0.1)',
            },
          },
          '& .ql-toolbar': {
            backgroundColor: '#fafafa',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            padding: '10px 12px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            '& .ql-formats': {
              marginRight: '8px',
              display: 'flex',
              gap: '2px',
            },
            '& button': {
              width: '28px !important',
              height: '28px !important',
              padding: '4px !important',
              borderRadius: '4px',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(0, 121, 142, 0.08)',
              },
              '&.ql-active': {
                backgroundColor: 'rgba(0, 121, 142, 0.12)',
                color: '#00798E',
              },
            },
            '& .ql-stroke': {
              stroke: 'rgba(0, 0, 0, 0.6)',
            },
            '& .ql-fill': {
              fill: 'rgba(0, 0, 0, 0.6)',
            },
            '& .ql-picker': {
              height: '28px',
              '& .ql-picker-label': {
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(0, 121, 142, 0.08)',
                },
              },
            },
          },
          '& .ql-container': {
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            fontSize: '14px',
            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
            minHeight: height,
            maxHeight: '400px',
            overflow: 'auto',
          },
          '& .ql-editor': {
            minHeight: height,
            maxHeight: '400px',
            padding: '14px 16px',
            lineHeight: '1.6',
            textTransform: 'uppercase',
            overflowY: 'auto',
            '&.ql-blank::before': {
              color: 'rgba(0, 0, 0, 0.38)',
              fontStyle: 'italic',
              fontSize: '14px',
              textTransform: 'none',
            },
            '& *': {
              textTransform: 'uppercase',
            },
            '& p': {
              marginBottom: '8px',
            },
            '& ul, & ol': {
              paddingLeft: '24px',
              marginBottom: '8px',
            },
            '& h1, & h2, & h3': {
              marginTop: '12px',
              marginBottom: '8px',
              fontWeight: 600,
            },
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
              '&:hover': {
                background: '#555',
              },
            },
          },
        }}
      >
        <ReactQuill
          theme="snow"
          value={value || ''}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
        />
      </Box>
      {helperText && (
        <FormHelperText error={error} sx={{ ml: 1.5, mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  height: PropTypes.string,
};

export default RichTextEditor;
